import { useEffect, useState } from 'react';

import CircularProgress from '@material-ui/core/CircularProgress';
import Typography from '@material-ui/core/Typography';
import Box from '@material-ui/core/Box';
import { makeStyles } from '@material-ui/core/styles';

import { JobRepairNoteDto, JobRepairNoteType } from '@vatos-pas/common';
import { Identifier, useDataProvider, useNotify } from 'react-admin';
import { Show } from 'components/Show';
import { getNoteType } from '../utils/getNoteType';

type NotesHistoryProps = {
  jobRepairId: Identifier | undefined;
  preloadedNotes?: JobRepairNoteDto[];
};

export const NotesHistory = ({
  jobRepairId,
  preloadedNotes,
}: NotesHistoryProps) => {
  const dataProvider = useDataProvider();
  const notify = useNotify();
  const classes = useStyles();

  const [loading, setLoading] = useState(false);
  const [notes, setNotes] = useState<JobRepairNoteDto[] | null>(
    preloadedNotes || null,
  );

  const descriptionNotes = notes?.filter(
    note =>
      'text' in note &&
      note.jobRepairNoteType === JobRepairNoteType.DESCRIPTION,
  );

  const getNotes = async () => {
    try {
      setLoading(true);
      const notesResponse = await dataProvider.getList<JobRepairNoteDto>(
        '/job-repair-note',
        {
          filter: {
            jobRepairId,
            'jobRepairNoteType||$in': [
              JobRepairNoteType.NOTE_BUMPOUT,
              JobRepairNoteType.DESCRIPTION,
            ],
          },
          pagination: { page: 1, perPage: 1000 },
          sort: { field: 'createdAt', order: 'DESC' },
        },
      );

      setNotes(notesResponse.data);
    } catch (err) {
      notify('Failed to get notes', 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // Only fetch if we don't have preloaded notes
    if (jobRepairId && !preloadedNotes) {
      getNotes();
    }
  }, [jobRepairId, preloadedNotes]);

  return (
    <Box width="100%" paddingBottom={1}>
      {loading ? (
        <Box display="flex" justifyContent="center">
          <CircularProgress size={24} />
        </Box>
      ) : (
        <Box>
          <Show
            condition={!!notes?.length}
            fallback={<Typography>No notes available.</Typography>}
          >
            <Box pt={2}>
              <Typography style={{ fontWeight: 'bold' }}>
                Notes History
              </Typography>
            </Box>
            {notes?.map((item, index) => {
              const isFirstDescription =
                !!descriptionNotes?.length &&
                item.id === descriptionNotes?.[descriptionNotes.length - 1].id;

              const noteType =
                'text' in item ? getNoteType(item, isFirstDescription) : '';

              const isDescriptionRemoved =
                noteType === 'Description Update' && !item.text?.length;

              return (
                <Box
                  key={item.id}
                  mb={2}
                  borderTop={index === 0 ? '' : 1}
                  borderColor="lightgray"
                  pt={2}
                >
                  <Box>
                    <Typography variant="caption">{noteType}</Typography>
                    <Box>
                      <Typography>{`${item.user?.firstName} ${
                        item.user?.lastName
                      } - ${new Date(item.createdAt).toLocaleTimeString(
                        'en-US',
                      )} ${new Date(item.createdAt).toLocaleDateString(
                        'en-US',
                      )}`}</Typography>
                    </Box>
                    <Box mt={2}>
                      <Typography
                        className={
                          isDescriptionRemoved ? classes.italic : undefined
                        }
                      >
                        {isDescriptionRemoved
                          ? 'Description removed'
                          : item.text}
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              );
            })}
          </Show>
        </Box>
      )}
    </Box>
  );
};

const useStyles = makeStyles({
  italic: {
    fontStyle: 'italic',
  },
});

export default NotesHistory;
